"use client";
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTheme } from "../../Components/ThemeContext";
import SEOHead from "../../Components/SEOHead";

// ─── JSON-LD Schema ───────────────────────────────────────────────────────────
const termsSchema = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Terms of Service — Fiieve",
  "url": "https://fiieve.com/terms-of-service",
  "description": "Read Fiieve's Terms of Service governing the use of our website and software development, engineering, AI automation, and IT consultancy services.",
  "publisher": { "@type": "Organization", "name": "Fiieve", "url": "https://fiieve.com" },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home",             "item": "https://fiieve.com/" },
      { "@type": "ListItem", "position": 2, "name": "Terms of Service", "item": "https://fiieve.com/terms-of-service" }
    ]
  }
});

const lightGridStyle = {
  backgroundImage: `linear-gradient(rgba(74,222,128,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(74,222,128,0.07) 1px, transparent 1px)`,
  backgroundSize: "40px 40px",
};
const darkGridStyle = {
  backgroundImage: `linear-gradient(rgba(74,222,128,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(251,146,60,0.05) 1px, transparent 1px)`,
  backgroundSize: "40px 40px",
};
const greenOrangeGridStyle = {
  backgroundImage: `linear-gradient(rgba(74,222,128,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(251,146,60,0.10) 1px, transparent 1px)`,
  backgroundSize: "40px 40px",
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const Section = ({ id, title, children, accentText, cardBg, bodyText }) => (
  <motion.section
    id={id}
    aria-labelledby={`${id}-heading`}
    variants={fadeUp}
    className={`mb-6 ${cardBg} border border-green-400/20 rounded-2xl p-6`}
  >
    <h2 id={`${id}-heading`} className={`text-xl font-bold ${accentText} mb-3`}>{title}</h2>
    <div className={`${bodyText} text-sm leading-relaxed space-y-2`}>{children}</div>
  </motion.section>
);

export default function TermsOfService() {
  const { isDark, theme } = useTheme();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const isGreenOrange = theme === "greenOrange";
  const isLight       = !isDark && !isGreenOrange;

  const sectionBg   = isDark ? "bg-black text-white" : isGreenOrange ? "bg-[#0d1a0e] text-white" : "bg-[#f0f4f0] text-gray-900";
  const sectionGrid = isLight ? lightGridStyle : isGreenOrange ? greenOrangeGridStyle : darkGridStyle;
  const accentText  = isGreenOrange ? "text-green-400" : isDark ? "text-green-400" : "text-green-700";
  const headingText = isDark || isGreenOrange ? "text-white" : "text-gray-900";
  const bodyText    = isDark || isGreenOrange ? "text-white/75" : "text-gray-800";
  const mutedText   = isDark || isGreenOrange ? "text-white/50" : "text-gray-500";
  const cardBg      = isDark ? "bg-gray-900/60" : isGreenOrange ? "bg-[#1a2e1a]" : "bg-[#e8f5e9]/80";
  const borderColor = isDark || isGreenOrange ? "border-green-400/20" : "border-green-600/20";

  return (
    <>
      <SEOHead
        title="Terms of Service | Fiieve — Software & Engineering Company"
        description="Read Fiieve's Terms of Service governing the use of our website and software development, engineering, AI automation, IoT, and IT consultancy services. Governed by the laws of England and Wales."
        canonical="https://fiieve.com/terms-of-service"
        schema={termsSchema}
      />

      <main
        className={`relative overflow-hidden min-h-screen pt-20 sm:pt-24 lg:pt-28 transition-colors duration-300 ${sectionBg}`}
        style={sectionGrid}
      >
        {isLight && (
          <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: 0 }}>
            <div style={{ position:"absolute", top:"5%", left:"-8%", width:"45vw", height:"45vw", background:"radial-gradient(circle, rgba(74,222,128,0.11) 0%, transparent 70%)", filter:"blur(45px)" }} />
            <div style={{ position:"absolute", top:"5%", right:"-8%", width:"40vw", height:"40vw", background:"radial-gradient(circle, rgba(251,146,60,0.09) 0%, transparent 70%)", filter:"blur(45px)" }} />
            <div style={{ position:"absolute", top:"50%", left:"20%", width:"60vw", height:"40vw", background:"radial-gradient(ellipse, rgba(74,222,128,0.07) 0%, transparent 65%)", filter:"blur(55px)" }} />
          </div>
        )}
        {isGreenOrange && (
          <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: 0 }}>
            <div style={{ position:"absolute", top:"5%", left:"-8%", width:"45vw", height:"45vw", background:"radial-gradient(circle, rgba(74,222,128,0.13) 0%, transparent 70%)", filter:"blur(45px)" }} />
            <div style={{ position:"absolute", top:"5%", right:"-8%", width:"40vw", height:"40vw", background:"radial-gradient(circle, rgba(251,146,60,0.14) 0%, transparent 70%)", filter:"blur(45px)" }} />
            <div style={{ position:"absolute", top:"50%", left:"20%", width:"60vw", height:"40vw", background:"radial-gradient(ellipse, rgba(74,222,128,0.08) 0%, transparent 65%)", filter:"blur(55px)" }} />
          </div>
        )}

        <div className="relative" style={{ zIndex: 1 }}>

          {/* ── HERO BANNER ── */}
          <div className="relative h-[25vh] overflow-hidden" style={{ marginBottom: "-3px" }}>
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/Images/bg.png')" }} aria-hidden="true" />
            <div className="absolute inset-0 bg-black/80" aria-hidden="true" />
            <div className="relative mx-auto max-w-7xl px-6 md:px-8 h-full flex flex-col items-center justify-center text-center">
              {/* ✅ SEO: H1 */}
              <h1 className="text-3xl md:text-5xl font-extrabold text-green-400">Terms of Service</h1>
              <nav aria-label="Breadcrumb" className="mt-3">
                <ol className="flex items-center justify-center gap-1 text-sm text-white">
                  <li><a href="/" className="hover:text-green-400 transition-colors">Home</a></li>
                  <li aria-hidden="true" className="text-gray-400">/</li>
                  <li aria-current="page" className="text-green-400">Terms of Service</li>
                </ol>
              </nav>
            </div>
          </div>

          {/* ── CONTENT ── */}
          <div className="max-w-6xl mx-auto px-6 py-10">

            {/* Intro */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-6">
              <p className={`text-xs ${mutedText} mb-3`}>
                Last updated: {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </p>
              <p className={`${bodyText} text-sm leading-relaxed mb-3`}>
                Please read these Terms of Service carefully before using the <strong className={headingText}>Fiieve</strong> website located at <a href="https://fiieve.com" className={`${accentText} hover:underline`}>fiieve.com</a> or engaging our software development, web development, mobile app, AI automation, IoT, embedded systems, or engineering services.
              </p>
              <p className={`${bodyText} text-sm leading-relaxed mb-3`}>
                By accessing our website or using our services, you understand and expressly agree to be legally bound by these Terms of Service and our <Link to="/privacy-policy" className={`${accentText} hover:underline`}>Privacy Policy</Link>. These Terms shall supersede any subsequent terms or conditions included with any purchase order, unless signed in writing by an authorised representative of Fiieve.
              </p>
              <p className={`${bodyText} text-sm leading-relaxed`}>
                If you do not agree to these Terms, please do not use our website or services. We reserve the right to change these Terms at any time, effective immediately upon posting. If you violate these Terms, we may terminate your use of the site and take appropriate legal action.
              </p>
            </motion.div>

            <motion.div
              variants={{ show: { transition: { staggerChildren: 0.08 } } }}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            >

              <Section id="acceptance" title="1. Acceptance of Terms" accentText={accentText} cardBg={cardBg} bodyText={bodyText}>
                <p>By accessing and using Fiieve's website and services, you confirm that you are at least 18 years of age, have the legal authority to enter into a binding agreement, and agree to be bound by these Terms of Service and our Privacy Policy. If you are using our services on behalf of a company or organisation, you represent that you have the authority to bind that entity to these Terms.</p>
              </Section>

              <Section id="services" title="2. Services Provided" accentText={accentText} cardBg={cardBg} bodyText={bodyText}>
                <p>Fiieve provides software development and digital engineering services including but not limited to:</p>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>Mobile app development for Android and iOS platforms</li>
                  <li>Web development — websites, web applications, and SaaS platforms</li>
                  <li>Graphic design and UI/UX design</li>
                  <li>Custom software development tailored to business requirements</li>
                  <li>AI automation workflows and intelligent agent systems</li>
                  <li>IoT development — hardware, firmware, and cloud integration</li>
                  <li>Embedded systems development and firmware engineering</li>
                  <li>Robotics, PCB design, and smart home automation</li>
                  <li>IT consultancy and technical advisory services</li>
                </ul>
                <p className="mt-2">The specific scope, deliverables, timeline, and cost of services are defined in individual project proposals or statements of work agreed in writing between the parties.</p>
              </Section>

              <Section id="website-use" title="3. Acceptable Use of Website" accentText={accentText} cardBg={cardBg} bodyText={bodyText}>
                <p>You agree to use our website only for lawful purposes. You must not:</p>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>Use our website in any way that violates applicable local, national, or international laws or regulations</li>
                  <li>Transmit any unsolicited or unauthorised advertising or promotional material (spam)</li>
                  <li>Attempt to gain unauthorised access to any part of our website, server, or connected systems</li>
                  <li>Engage in any conduct that disrupts, damages, or impairs the website or our infrastructure</li>
                  <li>Use automated tools (bots, scrapers) to extract data from our website without prior written consent</li>
                  <li>Deep-link to any page on our website without our express written authorisation</li>
                  <li>Reproduce, download, publish, or distribute any content from our website without prior written permission</li>
                </ul>
                <p className="mt-2">Users may download pages for personal use on a single device only, provided no modifications are made and full attribution to Fiieve is maintained.</p>
              </Section>

              <Section id="client-responsibilities" title="4. Client Responsibilities" accentText={accentText} cardBg={cardBg} bodyText={bodyText}>
                <p>As a client engaging Fiieve's services, you agree to:</p>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>Provide accurate, complete, and timely information required for project delivery</li>
                  <li>Respond to requests for feedback, content, or approvals within agreed timeframes</li>
                  <li>Make payments in accordance with the agreed schedule</li>
                  <li>Not use our services for any unlawful, harmful, or prohibited purpose</li>
                  <li>Ensure you hold all necessary rights and licences for any materials, content, or third-party assets you provide to us</li>
                  <li>Designate a single point of contact responsible for approvals and communication throughout the project</li>
                </ul>
                <p className="mt-2">Delays caused by failure to meet these responsibilities may affect project timelines and Fiieve shall not be held liable for resulting delays.</p>
              </Section>

              <Section id="intellectual-property" title="5. Intellectual Property & Copyright" accentText={accentText} cardBg={cardBg} bodyText={bodyText}>
                <p>All content on the Fiieve website — including text, graphics, logos, icons, images, and software — is the property of Fiieve and is protected by copyright law. Unauthorised use is strictly prohibited.</p>
                <p className="mt-2">Regarding project deliverables:</p>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>Upon receipt of <strong className={headingText}>full payment</strong> of all agreed fees, Fiieve transfers ownership of the final custom deliverables to the client, unless otherwise stated in the project agreement.</li>
                  <li>Fiieve retains ownership of all <strong className={headingText}>pre-existing tools</strong>, frameworks, libraries, proprietary code, templates, and methodologies used or developed during the project.</li>
                  <li>Fiieve retains the right to display completed work in our portfolio and use it for promotional purposes unless the client requests confidentiality in writing prior to project commencement.</li>
                  <li>Any third-party components, plugins, or open-source libraries remain subject to their original licences.</li>
                </ul>
              </Section>

              <Section id="payment" title="6. Payment Terms" accentText={accentText} cardBg={cardBg} bodyText={bodyText}>
                <p>Payment terms are specified in individual project proposals. Standard terms include:</p>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>A <strong className={headingText}>deposit (typically 30–50%)</strong> is required before project work commences</li>
                  <li>Milestone payments may be structured throughout the project based on agreed deliverable stages</li>
                  <li>Final payment is due before the final deliverables are handed over</li>
                  <li>Invoices are due within the timeframe stated in the proposal (typically 7–14 days)</li>
                  <li>Late payments may result in work being paused until the outstanding balance is settled</li>
                  <li>All prices are exclusive of VAT and applicable taxes unless stated otherwise</li>
                  <li>Fiieve reserves the right to charge interest on overdue invoices in accordance with the Late Payment of Commercial Debts (Interest) Act 1998</li>
                </ul>
              </Section>

              <Section id="revisions" title="7. Revisions, Changes & Scope" accentText={accentText} cardBg={cardBg} bodyText={bodyText}>
                <p>The number of included revisions is specified in each project proposal. The following applies to all projects:</p>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>Revisions within the agreed scope are included as stated in the proposal</li>
                  <li>Additional revisions or requests that fall outside the agreed scope constitute a scope change</li>
                  <li>All scope changes must be agreed in writing (email is acceptable) and may result in additional time and cost</li>
                  <li>Fiieve will provide a written estimate for any out-of-scope work before proceeding</li>
                  <li>Significant scope changes may require a revised project timeline</li>
                </ul>
              </Section>

              <Section id="confidentiality" title="8. Confidentiality" accentText={accentText} cardBg={cardBg} bodyText={bodyText}>
                <p>Both parties agree to maintain strict confidentiality regarding any proprietary, sensitive, or non-public information shared during the course of the project, including but not limited to business strategies, technical specifications, pricing, client data, and trade secrets.</p>
                <p className="mt-2">This confidentiality obligation:</p>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>Survives the completion or termination of any project agreement</li>
                  <li>Does not apply to information that is publicly available or independently known by either party</li>
                  <li>May be formalised through a separate Non-Disclosure Agreement (NDA) on request</li>
                </ul>
              </Section>

              <Section id="disclaimer" title="9. Warranties & Disclaimers" accentText={accentText} cardBg={cardBg} bodyText={bodyText}>
                <p>Fiieve warrants that services will be performed with reasonable skill, care, and professional competence. However:</p>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>The website and its content are provided <strong className={headingText}>"as is"</strong> without warranties of any kind, express or implied</li>
                  <li>We do not guarantee that our website will be uninterrupted, error-free, or free from viruses</li>
                  <li>We do not endorse or certify any information provided by third parties referenced on our website</li>
                  <li>References to any third-party products, services, or organisations do not constitute endorsement by Fiieve</li>
                  <li>Reliance on any information from our website is at your own risk. Independent expert review is recommended for all critical decisions.</li>
                </ul>
              </Section>

              <Section id="liability" title="10. Limitation of Liability" accentText={accentText} cardBg={cardBg} bodyText={bodyText}>
                <p>To the fullest extent permitted by law, Fiieve's total liability in connection with any project or use of our website is limited to the total fees paid by the client for that specific project.</p>
                <p className="mt-2">Fiieve shall not be liable for any:</p>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>Indirect, incidental, special, consequential, or punitive damages</li>
                  <li>Loss of profits, revenue, data, goodwill, or business opportunities</li>
                  <li>Damages arising from third-party services, tools, or integrations used in delivery</li>
                  <li>Delays or failures caused by circumstances beyond our reasonable control (force majeure)</li>
                  <li>Damages arising from client-provided materials that infringe third-party intellectual property rights</li>
                </ul>
              </Section>

              <Section id="termination" title="11. Termination" accentText={accentText} cardBg={cardBg} bodyText={bodyText}>
                <p>Either party may terminate a project agreement with written notice. In the event of termination:</p>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>The client is responsible for payment of all work completed up to the termination date</li>
                  <li>Fiieve will deliver all completed work and assets upon receipt of outstanding payment</li>
                  <li>Any deposit paid for uncommenced work may be subject to an administration fee</li>
                  <li>Fiieve reserves the right to terminate services immediately if the client breaches these Terms, engages in unlawful conduct, or fails to make payment after reasonable notice</li>
                </ul>
              </Section>

              <Section id="third-party" title="12. Third-Party Links & Services" accentText={accentText} cardBg={cardBg} bodyText={bodyText}>
                <p>Our website may contain links to third-party websites. These links are provided for convenience only. Fiieve does not endorse, approve, or certify the content of any external site and is not responsible for the accuracy, completeness, or reliability of information found there.</p>
                <p className="mt-2">Use of any information obtained from linked third-party sites is entirely at your own risk. Fiieve expressly disclaims all liability for damages arising from your use of linked external resources.</p>
              </Section>

              <Section id="severability" title="13. Severability" accentText={accentText} cardBg={cardBg} bodyText={bodyText}>
                <p>If any provision of these Terms of Service is found to be invalid, illegal, or unenforceable by a court of competent jurisdiction, that provision shall be severed from the remaining Terms. The remaining provisions shall continue in full force and effect, and shall be construed as if the invalid provision had been omitted.</p>
              </Section>

              <Section id="governing-law" title="14. Governing Law & Jurisdiction" accentText={accentText} cardBg={cardBg} bodyText={bodyText}>
                <p>These Terms of Service are governed by and construed in accordance with the <strong className={headingText}>laws of England and Wales</strong>. Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>
                <p className="mt-2">For clients based in the European Union, nothing in these Terms affects your statutory rights under applicable EU consumer protection legislation.</p>
              </Section>

              <Section id="changes" title="15. Changes to These Terms" accentText={accentText} cardBg={cardBg} bodyText={bodyText}>
                <p>Fiieve reserves the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to our website with an updated date. We will endeavour to notify registered clients of material changes via email.</p>
                <p className="mt-2">Your continued use of our website or services after any changes are posted constitutes your acceptance of the revised Terms. We encourage you to review this page periodically.</p>
              </Section>

              <Section id="contact" title="16. Contact Us" accentText={accentText} cardBg={cardBg} bodyText={bodyText}>
                <p>If you have any questions about these Terms of Service or wish to raise a concern, please contact us:</p>
                <ul className="list-none space-y-2 mt-3">
                  <li className="flex items-center gap-2">
                    <span>📧</span>
                    <a href="mailto:info@fiieve.com" className={`${accentText} hover:underline`}>info@fiieve.com</a>
                  </li>
                  <li className="flex items-center gap-2">
                    <span>📞</span>
                    <a href="tel:+447466761773" className={`${accentText} hover:underline`}>+44 7466 761773</a>
                  </li>
                  <li className="flex items-center gap-2">
                    <span>📍</span>
                    <span className={bodyText}>15 Kingston Road, Ilford, Essex IG1 1PA, London, United Kingdom</span>
                  </li>
                </ul>
                <p className="mt-3">We aim to respond to all enquiries within <strong className={headingText}>5 business days</strong>.</p>
              </Section>

            </motion.div>

            {/* ── FOOTER NAV ── */}
            <div className={`mt-8 pt-6 border-t ${borderColor} flex flex-wrap gap-6 text-sm`}>
              <Link to="/" className={`${accentText} hover:underline`}>← Back to Home</Link>
              <Link to="/privacy-policy" className={`${accentText} hover:underline`}>Privacy Policy →</Link>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}